import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gruposApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Plus, Pencil, Trash2, Layers, Building2, X, MapPin, Users, FileText, Loader2,
} from 'lucide-react';

const fmtNum = (n) => (Number(n ?? 0)).toLocaleString('es-ES');

const Grupos = () => {
  const navigate = useNavigate();
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', descripcion: '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // Iter61 — panel de detalle lateral
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const list = await gruposApi.list();
      setGrupos(list);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const abrirNuevo = () => {
    setEditing(null); setForm({ nombre: '', descripcion: '' }); setErr(''); setDialogOpen(true);
  };
  const abrirEditar = (g) => {
    setEditing(g); setForm({ nombre: g.nombre, descripcion: g.descripcion || '' });
    setErr(''); setDialogOpen(true);
  };

  const guardar = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const payload = { nombre: form.nombre.trim(), descripcion: form.descripcion || null };
      if (editing) {
        await gruposApi.update(editing.id, payload);
      } else {
        await gruposApi.create(payload);
      }
      setDialogOpen(false); cargar();
    } catch (ex) {
      setErr(ex.message);
    } finally { setSaving(false); }
  };

  const borrar = async (g) => {
    if (g.num_entidades > 0) {
      alert(`No se puede borrar: ${g.num_entidades} cliente${g.num_entidades !== 1 ? 's' : ''} pertenece${g.num_entidades !== 1 ? 'n' : ''} a este grupo. Cambia el grupo de esos clientes primero.`);
      return;
    }
    if (!window.confirm(`¿Eliminar el grupo "${g.nombre}"? Esta acción es irreversible.`)) return;
    try { await gruposApi.delete(g.id); cargar(); }
    catch (e) { alert(e.message); }
  };

  // Iter61 — abrir panel detalle
  const abrirDetalle = async (g) => {
    setDetalle({ grupo: { nombre: g.nombre, descripcion: g.descripcion }, entidades: [], resumen: null });
    setDetalleLoading(true);
    try {
      const data = await gruposApi.detalle(g.id);
      setDetalle(data);
    } catch (e) {
      setDetalle({ grupo: g, entidades: [], resumen: null, error: e.message });
    } finally {
      setDetalleLoading(false);
    }
  };

  return (
    <div data-testid="grupos-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900">Grupos empresariales</h1>
          <p className="text-ink-500 mt-1">
            Contenedor lógico para agrupar clientes de la misma matriz (p. ej. Grupo Veolia agrupa sus filiales con distinto CIF).
          </p>
        </div>
        <Button
          className="bg-brand-500 hover:bg-brand-600 text-white shadow-sm"
          onClick={abrirNuevo}
          data-testid="btn-nuevo-grupo"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nuevo grupo
        </Button>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-ink-400 bg-white border-ink-200">Cargando…</Card>
      ) : grupos.length === 0 ? (
        <Card className="p-10 text-center text-ink-400 bg-white border-ink-200">
          <Layers className="w-10 h-10 mx-auto mb-3 text-ink-300" />
          Aún no hay grupos. Crea el primero con el botón de arriba.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="grupos-grid">
          {grupos.map((g) => (
            <Card
              key={g.id}
              className="p-5 border-ink-200 bg-white card-hover cursor-pointer"
              data-testid={`grupo-${g.id}`}
              onClick={() => abrirDetalle(g)}
            >
              <div className="flex items-start justify-between mb-3" onClick={(e) => e.stopPropagation()}>
                <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                  <Layers className="w-5 h-5" />
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); abrirEditar(g); }}
                    title="Editar"
                    className="p-2 rounded hover:bg-ink-100 text-ink-600"
                    data-testid={`btn-edit-grupo-${g.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); borrar(g); }}
                    disabled={g.num_entidades > 0}
                    title={g.num_entidades > 0 ? 'Tiene clientes asignados' : 'Eliminar'}
                    className="p-2 rounded hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`btn-delete-grupo-${g.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="font-heading font-semibold text-ink-900 text-lg mb-1">{g.nombre}</h3>
              {g.descripcion && <p className="text-sm text-ink-500 mb-3 line-clamp-2">{g.descripcion}</p>}
              <div className="flex items-center gap-4 pt-3 border-t border-ink-100 text-sm">
                <span className="inline-flex items-center gap-1 text-ink-700">
                  <Building2 className="w-4 h-4 text-ink-400" />
                  <span className="font-semibold">{g.num_entidades}</span>
                </span>
                <span className="inline-flex items-center gap-1 text-ink-700" data-testid={`grupo-row-municipios-${g.id}`}>
                  <MapPin className="w-4 h-4 text-ink-400" />
                  <span className="font-semibold">{fmtNum(g.num_municipios || 0)}</span> mun.
                </span>
                <span className="inline-flex items-center gap-1 text-ink-700" data-testid={`grupo-row-poblacion-${g.id}`}>
                  <Users className="w-4 h-4 text-ink-400" />
                  <span className="font-semibold">{fmtNum(g.poblacion_total || 0)}</span> hab.
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) setDialogOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="dialog-grupo">
          <form onSubmit={guardar}>
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar grupo' : 'Nuevo grupo empresarial'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Actualiza el nombre y la descripción.' : 'Crea un contenedor para agrupar varios clientes.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs font-medium text-ink-600">Nombre *</label>
                <Input
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Ej: Grupo Agbar"
                  required minLength={2}
                  data-testid="grupo-form-nombre"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600">Descripción</label>
                <Textarea
                  rows={3}
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Breve nota interna sobre el grupo…"
                  data-testid="grupo-form-descripcion"
                />
              </div>
              {err && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" disabled={saving} data-testid="btn-guardar-grupo">
                {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear grupo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Iter61 — Panel de detalle lateral */}
      {detalle && (
        <div className="fixed inset-0 z-40" data-testid="grupo-detalle-panel">
          <div className="absolute inset-0 bg-ink-900/40" onClick={() => setDetalle(null)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-ink-100 px-6 py-4 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-heading font-bold text-ink-900">{detalle.grupo?.nombre}</h2>
                {detalle.grupo?.descripcion && (
                  <p className="text-sm text-ink-500 mt-0.5">{detalle.grupo.descripcion}</p>
                )}
                {detalle.resumen && (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <Badge className="bg-brand-50 text-brand-700 border border-brand-200">
                      {detalle.resumen.total_entidades} filial{detalle.resumen.total_entidades !== 1 ? 'es' : ''}
                    </Badge>
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {detalle.resumen.entidades_cliente} cliente{detalle.resumen.entidades_cliente !== 1 ? 's' : ''}
                    </Badge>
                    {detalle.resumen.entidades_no_cliente > 0 && (
                      <Badge className="bg-ink-50 text-ink-700 border border-ink-200">
                        {detalle.resumen.entidades_no_cliente} lead{detalle.resumen.entidades_no_cliente !== 1 ? 's' : ''}
                      </Badge>
                    )}
                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
                      {fmtNum(detalle.resumen.total_municipios)} municipios
                    </Badge>
                    <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {fmtNum(detalle.resumen.total_poblacion)} hab.
                    </Badge>
                    {detalle.resumen.contratos_vencen_12m > 0 && (
                      <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                        {detalle.resumen.contratos_vencen_12m} contratos vencen &lt;12m
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setDetalle(null)}
                className="p-2 rounded hover:bg-ink-100 text-ink-600"
                aria-label="Cerrar"
                data-testid="btn-grupo-detalle-cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {detalleLoading ? (
                <div className="py-8 text-center text-ink-400">
                  <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Cargando filiales…
                </div>
              ) : detalle.entidades?.length === 0 ? (
                <Card className="p-10 text-center text-ink-400 bg-ink-50 border-ink-100">
                  Este grupo aún no tiene filiales asignadas.
                </Card>
              ) : (
                <table className="w-full text-sm" data-testid="grupo-detalle-tabla-entidades">
                  <thead>
                    <tr className="text-left text-xs text-ink-500 border-b border-ink-100">
                      <th className="py-2 pr-3">Razón social</th>
                      <th className="py-2 pr-3">CIF</th>
                      <th className="py-2 pr-3">División</th>
                      <th className="py-2 pr-3">Estado</th>
                      <th className="py-2 pr-3 text-right">Municipios</th>
                      <th className="py-2 pr-3 text-right">Población</th>
                      <th className="py-2 pr-0 text-right">Vencen &lt;12m</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.entidades.map((e) => (
                      <tr
                        key={e.id}
                        className="border-b border-ink-100 hover:bg-ink-50 cursor-pointer"
                        onClick={() => navigate(`/clientes/${e.id}`)}
                        data-testid={`grupo-fila-${e.id}`}
                      >
                        <td className="py-2.5 pr-3 font-medium text-ink-900">{e.razon_social}</td>
                        <td className="py-2.5 pr-3 text-ink-500 font-mono text-xs">{e.cif || '—'}</td>
                        <td className="py-2.5 pr-3">
                          {e.division
                            ? <Badge className="bg-brand-50 text-brand-700 border border-brand-200">{e.division}</Badge>
                            : <span className="text-ink-300">—</span>}
                        </td>
                        <td className="py-2.5 pr-3">
                          {e.es_cliente ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">CLIENTE</Badge>
                          ) : e.estado === 'prospecto' ? (
                            <Badge className="bg-blue-50 text-blue-700 border border-blue-200">PROSPECTO</Badge>
                          ) : (
                            <Badge className="bg-ink-100 text-ink-600 border border-ink-200">{(e.estado || 'LEAD').toUpperCase()}</Badge>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{fmtNum(e.num_municipios)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{fmtNum(e.poblacion_total)}</td>
                        <td className="py-2.5 pr-0 text-right tabular-nums">
                          {e.num_contratos_vencen_12m > 0 ? (
                            <span className="text-amber-700 font-semibold">{e.num_contratos_vencen_12m}</span>
                          ) : <span className="text-ink-300">0</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Grupos;
